import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, download } from '@/lib/api';
import type { ColumnMeta, FieldDef, ScreenDef } from '@/lib/types';
import type { GridColumn } from '@/grid/types';
import { DataGrid, columnsFromMeta } from '@/grid/DataGrid';
import { Spinner, ErrorBox } from '@/ui/Spinner';
import { ReportMenu } from '@/reports/ReportMenu';
import { useWorkspace } from '@/shell/workspace';
import { recordFormFor } from './custom/registry';
import { confirmDialog } from '@/ui/confirm';
import { toast } from 'sonner';
import { notifyDataChanged } from '@/grid/events';
import { DateInput } from '@/ui/DateInput';

interface SectionCol {
  alan: string; baslik?: string; genislik?: number; gorunur?: boolean; salt?: boolean; editor?: string;
  lookup?: { tablo?: string; anahtar: string; gosterim: string };
  combo?: { liste: (string | { deger: unknown; metin: string })[] };
}
interface Section {
  key: string; title: string | null; table: string | null; keyFields: string[]; parent: { bolum: string; alanlar: [string, string][] } | null;
  columns: SectionCol[]; canInsert: boolean; canUpdate: boolean; canDelete: boolean; fields: FieldDef[];
  /** Delphi formundaki tarih seçici (ör. Günlük Kasa İşlemleri cxDateEdit1) */
  parametre: { ad: string; baslik: string; ozet: boolean } | null;
}
interface Ozet { kapali: boolean; alanlar: [string, string][]; satir: Record<string, any> | null }

const bugun = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const para = (v: unknown) => Number(v ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Delphi tanım formlarından türetilmiş tablo ekranı (Web_Screen.EKRAN_TURU = 'tablo').
 * Her bölüm bir grid + veri seti; ust tanımlı bölümler master seçimine bağlı detaydır (MasterSource/MasterFields).
 */
export function TableScreen({ screen }: { screen: ScreenDef }) {
  const q = useQuery({ queryKey: ['table-def', screen.tag], queryFn: () => api<{ sections: Section[] }>(`/screens/${screen.tag}/table`) });
  const [selected, setSelected] = useState<Record<string, Record<string, any> | null>>({});
  if (q.isLoading) return <Spinner />;
  if (q.error) return <ErrorBox error={q.error} />;
  const sections = q.data!.sections;
  const roots = sections.filter((s) => !s.parent);
  const childrenOf = (k: string) => sections.filter((s) => s.parent?.bolum === k);

  const renderSection = (s: Section, depth: number): React.ReactNode => {
    const kids = childrenOf(s.key);
    const master = s.parent ? selected[s.parent.bolum] ?? null : null;
    return (
      <div key={s.key} className="flex min-h-0 min-w-0 flex-1 flex-col gap-2" style={{ minHeight: depth ? 220 : 260 }}>
        {(sections.length > 1) && <div className="px-1 text-xs font-semibold uppercase tracking-wide muted">{s.title ?? s.table}</div>}
        <SectionGrid screen={screen} s={s} master={master} onSelect={(r) => setSelected((x) => ({ ...x, [s.key]: r }))} />
        {kids.map((k) => renderSection(k, depth + 1))}
      </div>
    );
  };
  return (
    <div className="flex min-h-0 min-w-0 flex-1 gap-2 p-2">
      {roots.map((r) => renderSection(r, 0))}
    </div>
  );
}

function SectionGrid({ screen, s, master, onSelect }: { screen: ScreenDef; s: Section; master: Record<string, any> | null; onSelect: (r: Record<string, any> | null) => void }) {
  const [selected, setSel] = useState<Record<string, any> | null>(null);
  const [tarih, setTarih] = useState(bugun);
  const [yenile, setYenile] = useState(0);
  const params = s.parametre ? { [s.parametre.ad]: `${tarih}T00:00:00` } : undefined;
  const ozet = useQuery({
    queryKey: ['table-ozet', screen.tag, s.key, tarih, yenile],
    enabled: !!s.parametre,
    queryFn: () => api<Ozet>(`/screens/${screen.tag}/table/${s.key}/ozet`, { body: { params } }),
  });
  const kapali = !!ozet.data?.kapali;
  const ws = useWorkspace();
  const rf = !s.parent ? recordFormFor(screen) : null;
  const openRecord = (row: Record<string, any> | null) => rf && ws.open({ kind: 'custom', component: rf.component, title: row ? `${rf.title} · ${row[rf.titleField ?? 'ID'] ?? row.ID}` : `${rf.title} · Yeni`, props: { screen, id: row ? row[rf.idField ?? 'ID'] : null } }, { newInstance: !row });
  const removeRecord = async (row: Record<string, any>) => {
    if (!(await confirmDialog('Silmek İstediğinizden Eminmisiniz ?', { danger: true, ok: 'Sil' }))) return;
    try { await api(`/screens/${screen.tag}/kayit/${row.ID}/delete`, { body: { row } }); toast.success('Silindi'); notifyDataChanged(screen.tag); } catch (e: any) { toast.error(e.message); }
  };
  const lookups = useQuery({
    queryKey: ['table-lookups', screen.tag, s.key],
    queryFn: async () => {
      const out: Record<string, { value: unknown; label: string }[]> = {};
      for (const c of s.columns) if (c.lookup?.tablo) out[c.alan] = (await api(`/screens/${screen.tag}/table/${s.key}/lookup/${encodeURIComponent(c.alan)}`)).items;
      return out;
    },
  });
  const build = useMemo(() => (meta: ColumnMeta[]): GridColumn[] => {
    const base = new Map(columnsFromMeta(meta).map((c) => [c.key.toLocaleUpperCase('tr-TR'), c]));
    const fieldBy = new Map(s.fields.map((f) => [f.field.toLocaleUpperCase('tr-TR'), f]));
    const cols: GridColumn[] = s.columns.map((c) => {
      const b = base.get(c.alan.toLocaleUpperCase('tr-TR'));
      const f = fieldBy.get(c.alan.toLocaleUpperCase('tr-TR'));
      const col: GridColumn = {
        ...(b ?? { key: c.alan, caption: c.alan }),
        key: b?.key ?? c.alan,
        caption: f?.caption ?? c.baslik ?? c.alan,
        width: c.genislik ? Math.max(50, Math.round(c.genislik * 1.05)) : b?.width,
        hidden: c.gorunur === false || f?.hidden,
        readonly: c.salt || f?.readonly || b?.readonly,
        mask: f?.mask ?? b?.mask,
        total: f?.total ?? null,
      };
      if (c.editor === 'check') col.editor = 'check';
      if (c.editor === 'date') col.editor = 'date';
      if (c.editor === 'memo') col.editor = 'memo';
      if (c.editor === 'number') col.editor = 'number';
      if (c.lookup?.tablo) {
        const items = lookups.data?.[c.alan] ?? [];
        col.editor = 'combo';
        col.comboItems = items;
        col.display = (row) => { const it = items.find((i) => String(i.value) === String(row[col.key])); return it ? String(it.label ?? '') : row[col.key] == null ? '' : String(row[col.key]); };
      }
      if (c.combo?.liste?.length) {
        col.editor = 'combo';
        col.comboItems = c.combo.liste.map((i) => (typeof i === 'string' ? i : { value: i.deger, label: i.metin }));
      }
      return col;
    });
    // ekranda tanımsız kolonlar gizli olarak eklenir (kolon seçiciden açılabilir)
    for (const [k, b] of base) if (!s.columns.some((c) => c.alan.toLocaleUpperCase('tr-TR') === k)) cols.push({ ...b, hidden: true, readonly: true });
    return cols;
  }, [s, lookups.data]);

  const keyOf = (row: Record<string, any>) => Object.fromEntries((s.keyFields.length ? s.keyFields : ['ID']).map((k) => [k, row[k]]));
  const blocked = !!s.parent && !master;
  const sonra = <T,>(p: Promise<T>) => p.then((r) => { setYenile((x) => x + 1); return r; });
  const grid = (
    <DataGrid
      queryKey={['table', screen.tag, s.key, master ? JSON.stringify(master) : null, lookups.dataUpdatedAt, tarih]}
      onRowOpen={rf ? (r) => openRecord(r) : undefined}
      toolbarLeft={rf ? <>
        <button className="btn" disabled={!screen.rights.insert} onClick={() => openRecord(null)}>+ Yeni</button>
        <button className="btn" disabled={!selected} onClick={() => selected && openRecord(selected)}>Aç</button>
        <button className="btn btn-danger" disabled={!selected || !screen.rights.delete} onClick={() => selected && removeRecord(selected)}>Sil</button>
      </> : s.parametre ? <>
        <label className="flex items-center gap-2 text-sm"><span className="muted">{s.parametre.baslik}</span>
          <DateInput className="w-[150px]" value={`${tarih}T00:00:00`} onChange={(v) => v && setTarih(v.slice(0, 10))} /></label>
        {kapali && <span className="badge bg-[var(--warning-soft)] text-[var(--warning-text)]" title="Seçili tarih kasa kapanış tarihinden önce">Kasa kapalı</span>}
      </> : undefined}
      fetch={(q) => api(`/screens/${screen.tag}/table/${s.key}/list`, { body: { query: q, master, params } })}
      buildColumns={build}
      layoutKey={`tb:${screen.tag}:${s.key}`}
      onSelectionChange={(r) => { setSel(r); onSelect(r); }}
      emptyText={blocked ? 'Üst kayıttan seçim yapınız' : undefined}
      editing={rf ? undefined : {
        canInsert: s.canInsert && screen.rights.insert && !blocked && !kapali,
        canUpdate: s.canUpdate && screen.rights.update && !kapali,
        canDelete: s.canDelete && screen.rights.delete && !kapali,
        onSave: (row, original) => sonra(original
          ? api(`/screens/${screen.tag}/table/${s.key}/rows`, { method: 'PUT', body: { row, key: keyOf(original), master, params } })
          : api(`/screens/${screen.tag}/table/${s.key}/rows`, { body: { row, master, params } })),
        onDelete: (row) => sonra(api(`/screens/${screen.tag}/table/${s.key}/delete`, { body: { key: keyOf(row), row } })),
      }}
      toolbarRight={!s.parent ? <ReportMenu reports={screen.reports ?? []} row={selected} /> : undefined}
      onExport={(q, cols) => download(`/export/table/${screen.tag}/${s.key}`, { query: q, master, params, columns: cols.map((c) => ({ key: c.key, caption: c.caption, mask: c.mask, sqlType: c.sqlType })) }, `${s.title ?? s.table ?? 'liste'}.xlsx`)}
    />
  );
  if (!s.parametre?.ozet) return grid;
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      {grid}
      <div className="card flex flex-wrap items-center justify-end gap-x-6 gap-y-1 px-3 py-2 text-sm">
        {(ozet.data?.alanlar ?? []).map(([k, b]) => (
          <span key={k} className="flex items-baseline gap-2"><span className="muted">{b}</span><span className="font-semibold tabular-nums">{para(ozet.data?.satir?.[k])}</span></span>
        ))}
      </div>
    </div>
  );
}
